<template>
  <div class="ok-confirm-modal">
    <!-- overlay -->
    <div
      v-if="visible"
      class="overlay"
      @click="disableOutsideClick ? null : (closeEmitsClose ? $emit('close') : $emit('cancel'))"
    />

    <form
      v-if="visible"
      class="ok-modal"
      :class="`${customClass} ${size || ''}`"
      @change="$event ? null : $emit('cancel')"
      @submit.prevent="remove"
    >
      <div
        v-if="showTitle"
        class="ok-confirm-title"
      >
        <span>{{ titleText }}</span>

        <b-button
          class="btn-icon"
          variant="link"
          @click="closeEmitsClose ? $emit('close') : $emit('cancel')"
        >
          <feather-icon
            icon="XIcon"
            class="text-secondary"
            size="14"
          />
        </b-button>
      </div>

      <div class="ok-confirm-content">
        <error-alert :error="error" />
        <component
          :is="contentComponent"
          v-if="contentComponent"
          v-bind="boundContentProps"
        />
        <div>
          {{ text }}
        </div>
        <slot />
      </div>

      <div
        class="ok-confirm-footer"
        :class="{'inline-actions': inlineActions}"
      >
        <b-button
          variant="dark"
          block
          type="submit"
          :class="{ 'cursor-not-allowed': isConfirmDisabled }"
          :disabled="!!loading || isConfirmDisabled"
        >
          <b-spinner
            v-if="loading"
            small
          />
          <span v-else>{{ deleteLabel }}</span>
        </b-button>

        <b-button
          v-if="showCancel"
          class="cancel-btn"
          :class="{'mt-0': inlineActions}"
          variant="outline-dark"
          block
          :disabled="!!loading"
          @click="$emit('cancel')"
        >
          <span>{{ cancelLabel }}</span>
        </b-button>
        <b-button
          v-if="showConfirmAndPrint"
          class="conform-and-print-btn"
          :class="{'mt-0': inlineActions, 'cursor-not-allowed': isConfirmDisabled}"
          variant="outline-dark"
          block
          :disabled="!!loading || isConfirmDisabled"
          @click="confirmAndPrint"
        >
          <span>{{ confirmAndPrintLabel }}</span>
        </b-button>
      </div>
    </form>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import ErrorAlert from '@/@okmenu/components/alerts/ErrorAlert'
import i18n from '@/libs/i18n'

export default {
  name: 'OkConfirmModal',
  components: {
    ErrorAlert,
    BButton,
    BSpinner,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    preConfirm: {
      type: Function,
      default: null,
    },
    preConfirmAndPrint: {
      type: Function,
      default: null,
    },
    confirmButtonText: {
      type: String,
      default: '',
    },
    showCancel: {
      type: Boolean,
      default: false,
    },
    showConfirmAndPrint: {
      type: Boolean,
      default: false,
    },
    confirmAndPrintLabel: {
      type: String,
      default: '',
    },
    cancelButtonText: {
      type: String,
      default: '',
    },
    disableOutsideClick: {
      type: Boolean,
      default: false,
    },
    closeEmitsClose: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    disableConfirmButton: {
      type: [Boolean, Object],
      default: false,
    },
    inlineActions: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: '',
    },
    // eslint-disable-next-line vue/require-prop-types
    contentComponent: {
      default: null,
    },
    componentProps: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      visible: true,
      error: null,
      loading: false,
      extraConfirmDisabled: !!(this.componentProps && this.componentProps.showCancelReason),
    }
  },
  computed: {
    deleteLabel() {
      return this.confirmButtonText || i18n.t('forms.delete')
    },
    cancelLabel() {
      return this.cancelButtonText || i18n.t('forms.cancel')
    },
    titleText() {
      return this.title || i18n.t('forms.are-you-sure')
    },
    boundContentProps() {
      return {
        ...this.componentProps,
        setConfirmDisabled: this.setConfirmDisabled,
      }
    },
    isConfirmDisabled() {
      const val = this.disableConfirmButton
      const fromProp = val && typeof val === 'object' ? !!val.value : !!val
      return fromProp || this.extraConfirmDisabled
    },
  },
  methods: {
    setConfirmDisabled(disabled) {
      this.extraConfirmDisabled = !!disabled
    },
    close() {
      this.visible = false
      this.$emit('cancel')
    },
    remove() {
      if (this.loading || this.isConfirmDisabled) return null

      if (!this.preConfirm || typeof this.preConfirm !== 'function') {
        return this.$emit('confirm')
      }

      this.loading = true
      this.error = null
      return Promise.resolve(this.preConfirm())
        .then(() => this.$emit('confirm'))
        .catch(e => {
          this.error = e
        })
        .finally(() => {
          this.loading = false
        })
    },
    confirmAndPrint() {
      if (this.loading || this.isConfirmDisabled) return null
      if (!this.preConfirmAndPrint || typeof this.preConfirmAndPrint !== 'function') {
        this.$emit('confirmAndPrint')
        return this.close()
      }

      this.loading = true
      this.error = null

      return Promise.resolve(this.preConfirmAndPrint())
        .then(() => {
          this.$emit('confirmAndPrint')
          this.close()
        })
        .catch(e => {
          this.error = e
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss">
.ok-confirm-modal {
  position: static;

  .modal-body {
    padding-bottom: 20px;
  }

  .ok-confirm-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border-bottom: solid 1px #e8e7eb;
    margin-bottom: 13.5px;

    span {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.29;
      color: #6d6a7a;
    }
  }

  .ok-confirm-footer {
    margin-top: 15px;

    .btn {
      font-size: 14px !important;
      font-weight: 600 !important;
      line-height: 1.29 !important;
    }

    .cancel-btn {
      margin-top: 10px;
    }

    &.inline-actions {
      display: flex;
      justify-content: space-around;
      align-items: center;

      .btn {
        margin: 0 5px;
      }
    }

    .btn.cursor-not-allowed,
    .btn.cursor-not-allowed:disabled {
      cursor: not-allowed !important;
      pointer-events: auto !important;
    }
  }

  .ok-confirm-content {
    font-size: 14px;
    line-height: 1.25;
    font-weight: 500;
    margin-bottom: 5px;
    color: #aaa8b2;
  }

  .overlay {
    position: fixed;
    z-index: 9998;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, .5);
  }

  .ok-modal {
    border-radius: 5px;
    width: 100%;
    max-width: 480px;
    z-index: 9999;
    margin: 0 auto;
    padding: 20px 17.5px;
    background-color: var(--white);
    position: fixed;
    top: 20%;
    left: 50%;
    transform: translateX(-50%);

    &.sm {
      max-width: 380px;
    }

    &.md {
      max-width: 600px;
    }

    &.lg {
      max-width: 800px;
    }

    &.xl {
      max-width: 800px;
    }
  }
}
</style>
